import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { colors, fonts, shadows, radii } from "../theme";
import { warn } from "../lib/log";
import { useStudio } from "../store/useStudio";
import { locationLabel, systemLabel, TYPOLOGIES } from "../lib/kbLogic";
import { assistantApi } from "../api/assistant";

const msgId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const GREETING = {
  id: "greeting",
  role: "assistant",
  content:
    "I'm the Circularity Assistant. Describe your site and what you intend to grow, and I'll recommend crops and waste-to-resource loops suited to your climate and available area.",
};

export default function AIAssistant() {
  const { assistantSessionId, applySuggestions } = useStudio();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const buildContext = () => {
    const { kb, geo, weather, selections, areas, buildingTypology, occupancy } = useStudio.getState();
    const typo = TYPOLOGIES.find((t) => t.id === buildingTypology);
    return {
      site: geo?.name || null,
      dli: weather?.dli ?? null,
      ghi: weather?.ghi ?? null,
      temperature: weather?.temperature ?? null,
      typology: buildingTypology || null,
      typology_label: typo?.label || null,
      occupancy: occupancy || null,
      locations: selections.locations,
      location_labels: selections.locations.map((l) => locationLabel(kb, l)),
      systems: selections.systems,
      system_labels: selections.systems.map((s) => systemLabel(kb, s)),
      areas,
      crops: selections.crops,
      resources: selections.resources,
    };
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setError(null);
    setMessages((m) => [...m, { id: msgId(), role: "user", content: q }]);
    setLoading(true);
    try {
      const res = await assistantApi.chat({
        session_id: assistantSessionId,
        message: q,
        context: buildContext(),
      });
      setMessages((m) => [...m, { id: msgId(), role: "assistant", content: res.reply, suggestions: res.suggestions, applied: false }]);
    } catch (e) {
      warn("assistant failed", e);
      setError("The assistant is unavailable right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const applyFromMessage = (id, cropIds, resourceIds) => {
    applySuggestions({ crops: cropIds, resources: resourceIds });
    setMessages((m) => m.map((msg) => (msg.id === id ? { ...msg, applied: true, appliedCount: cropIds.length + resourceIds.length } : msg)));
  };

  const QUICK = ["Best crops for my site?", "Suggest circular waste loops", "What fits my allocated area?"];

  return (
    <>
      {/* Floating launcher */}
      <motion.button
        data-testid="assistant-fab"
        onClick={() => setOpen((v) => !v)}
        style={styles.fab}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Open Circularity Assistant"
      >
        <SparkIcon />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="assistant-panel"
            style={styles.panel}
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
          >
            <div style={styles.header}>
              <div style={styles.headerLeft}>
                <span style={styles.headerDot} />
                <div>
                  <div style={styles.headerTitle}>Circularity Assistant</div>
                  <div style={styles.headerSub}>Claude · calibrated to your site</div>
                </div>
              </div>
              <button style={styles.closeBtn} onClick={() => setOpen(false)} data-testid="assistant-close">×</button>
            </div>

            <div style={styles.messages} ref={scrollRef} data-testid="assistant-messages">
              {messages.map((m, i) => (
                <div key={m.id} data-testid={`assistant-msg-${i}`}>
                  <div style={m.role === "user" ? styles.userBubble : styles.aiBubble}>{m.content}</div>
                  {m.role === "assistant" && m.suggestions && hasPicks(m.suggestions) && (
                    <SuggestionCard
                      suggestions={m.suggestions}
                      applied={m.applied}
                      appliedCount={m.appliedCount}
                      onApply={(cropIds, resourceIds) => applyFromMessage(m.id, cropIds, resourceIds)}
                      testIndex={i}
                    />
                  )}
                </div>
              ))}
              {loading && (
                <div style={styles.aiBubble} data-testid="assistant-typing">
                  <TypingDots />
                </div>
              )}
              {error && <div style={styles.errorBubble} data-testid="assistant-error">{error}</div>}
            </div>

            {messages.length <= 1 && (
              <div style={styles.quickRow}>
                {QUICK.map((q) => (
                  <button key={q} style={styles.quickChip} onClick={() => send(q)} data-testid="assistant-quick">
                    {q}
                  </button>
                ))}
              </div>
            )}

            <div style={styles.inputRow}>
              <input
                data-testid="assistant-input"
                style={styles.input}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about crops, systems or loops…"
              />
              <button data-testid="assistant-send" style={styles.sendBtn} onClick={() => send()} disabled={loading || !input.trim()}>
                <ArrowIcon />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function hasPicks(s) {
  return (s.crops && s.crops.length) || (s.resources && s.resources.length);
}

function SuggestionCard({ suggestions, applied, appliedCount, onApply, testIndex }) {
  const crops = suggestions.crops || [];
  const resources = suggestions.resources || [];
  // Draft staging: everything starts kept; user reviews and rejects before hitting Done.
  const [keep, setKeep] = useState(() => {
    const init = {};
    crops.forEach((c) => { init[`c:${c.id}`] = true; });
    resources.forEach((r) => { init[`r:${r.id}`] = true; });
    return init;
  });
  const toggle = (k) => setKeep((s) => ({ ...s, [k]: !s[k] }));
  const keptCrops = crops.filter((c) => keep[`c:${c.id}`]).map((c) => c.id);
  const keptResources = resources.filter((r) => keep[`r:${r.id}`]).map((r) => r.id);
  const keptTotal = keptCrops.length + keptResources.length;

  if (applied) {
    return (
      <div style={styles.suggestCard} data-testid={`assistant-suggestion-${testIndex}`}>
        <div style={{ ...styles.applyBtn, ...styles.applyBtnDone, cursor: "default" }} data-testid={`assistant-applied-${testIndex}`}>
          ✓ Added {appliedCount} to your loops
        </div>
      </div>
    );
  }

  return (
    <div style={styles.suggestCard} data-testid={`assistant-suggestion-${testIndex}`}>
      <div style={styles.draftHead}>
        <span style={styles.draftHeadDot} />
        <span style={styles.draftHeadTitle}>DRAFTING WINDOW</span>
        <span style={styles.draftHeadHint}>select keep or reject for each, then confirm</span>
      </div>
      {crops.length > 0 && (
        <div style={styles.suggestGroup}>
          <div style={styles.suggestLabel}>CROPS</div>
          <div style={styles.chipWrap}>
            {crops.map((c) => {
              const on = keep[`c:${c.id}`];
              return (
                <button
                  key={c.id}
                  onClick={() => toggle(`c:${c.id}`)}
                  data-testid={`assistant-draft-crop-${c.id}`}
                  data-keep={on ? "true" : "false"}
                  style={{ ...styles.toggleChip, borderColor: colors.pillar.crops, ...(on ? { background: colors.pillar.crops, color: "#fff" } : { color: colors.pillar.crops, opacity: 0.55 }) }}
                >
                  <span style={styles.chipMark}>{on ? "✓" : "＋"}</span>{c.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {resources.length > 0 && (
        <div style={styles.suggestGroup}>
          <div style={styles.suggestLabel}>RESOURCE LOOPS</div>
          <div style={styles.chipWrap}>
            {resources.map((r) => {
              const on = keep[`r:${r.id}`];
              return (
                <button
                  key={r.id}
                  onClick={() => toggle(`r:${r.id}`)}
                  data-testid={`assistant-draft-resource-${r.id}`}
                  data-keep={on ? "true" : "false"}
                  style={{ ...styles.toggleChip, borderColor: colors.pillar.resources, ...(on ? { background: colors.pillar.resources, color: "#fff" } : { color: colors.pillar.resources, opacity: 0.55 }) }}
                >
                  <span style={styles.chipMark}>{on ? "✓" : "＋"}</span>{r.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <button
        onClick={() => onApply(keptCrops, keptResources)}
        disabled={keptTotal === 0}
        data-testid={`assistant-apply-${testIndex}`}
        style={{ ...styles.applyBtn, ...(keptTotal === 0 ? styles.applyBtnDisabled : {}) }}
      >
        {keptTotal === 0 ? "Select at least one" : `Done — add ${keptTotal} to my design`}
      </button>
    </div>
  );
}

function TypingDots() {
  return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          style={{ width: 6, height: 6, borderRadius: 999, background: colors.textTertiary, display: "inline-block" }}
          animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
          transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }}
        />
      ))}
    </span>
  );
}

function SparkIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" fill="#fff" />
      <circle cx="18.5" cy="5.5" r="1.4" fill="#fff" opacity="0.85" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M5 12h14M13 6l6 6-6 6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const CARD_W = 384;

const styles = {
  fab: {
    position: "fixed",
    right: 24,
    bottom: 154,
    width: 56,
    height: 56,
    borderRadius: 999,
    border: "none",
    cursor: "pointer",
    background: "linear-gradient(140deg, #51aafe 0%, #2E86D9 100%)",
    boxShadow: "0 10px 28px rgba(10,132,255,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 40,
  },
  panel: {
    position: "fixed",
    right: 24,
    bottom: 154,
    width: CARD_W,
    maxWidth: "94vw",
    height: 540,
    maxHeight: "72vh",
    display: "flex",
    flexDirection: "column",
    background: colors.glassStrong,
    backdropFilter: "blur(30px) saturate(180%)",
    WebkitBackdropFilter: "blur(30px) saturate(180%)",
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: radii.lg,
    boxShadow: shadows.float,
    overflow: "hidden",
    zIndex: 41,
  },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: `1px solid ${colors.border}` },
  headerLeft: { display: "flex", alignItems: "center", gap: 10 },
  headerDot: { width: 30, height: 30, borderRadius: 999, background: "linear-gradient(140deg, #51aafe, #2E86D9)", flexShrink: 0 },
  headerTitle: { fontFamily: fonts.sans, fontSize: 14.5, fontWeight: 700, color: colors.textPrimary, letterSpacing: -0.2 },
  headerSub: { fontFamily: fonts.mono, fontSize: 9.5, letterSpacing: 1, color: colors.textTertiary, marginTop: 1 },
  closeBtn: { width: 30, height: 30, borderRadius: 999, border: "none", background: "rgba(0,0,0,0.05)", color: colors.textSecondary, fontSize: 20, lineHeight: 1, cursor: "pointer" },
  messages: { flex: 1, overflowY: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: 12 },
  userBubble: { alignSelf: "flex-end", maxWidth: "82%", marginLeft: "auto", background: colors.textPrimary, color: "#fff", padding: "10px 13px", borderRadius: "16px 16px 4px 16px", fontFamily: fonts.sans, fontSize: 13.5, lineHeight: 1.45 },
  aiBubble: { alignSelf: "flex-start", maxWidth: "88%", background: "#fff", color: colors.textPrimary, padding: "11px 14px", borderRadius: "16px 16px 16px 4px", fontFamily: fonts.sans, fontSize: 13.5, lineHeight: 1.5, boxShadow: shadows.soft, border: `1px solid ${colors.border}`, whiteSpace: "pre-wrap" },
  errorBubble: { alignSelf: "flex-start", background: "rgba(255,59,48,0.08)", color: "#FF3B30", padding: "10px 13px", borderRadius: 12, fontFamily: fonts.sans, fontSize: 12.5 },
  suggestCard: { marginTop: 8, maxWidth: "88%", background: "rgba(255,255,255,0.7)", border: `1px solid ${colors.border}`, borderRadius: 14, padding: 12, boxShadow: shadows.soft },
  suggestGroup: { marginBottom: 10 },
  suggestLabel: { fontFamily: fonts.mono, fontSize: 9, letterSpacing: 1.4, color: colors.textTertiary, marginBottom: 6 },
  chipWrap: { display: "flex", flexWrap: "wrap", gap: 6 },
  chip: { fontFamily: fonts.sans, fontSize: 11, fontWeight: 600, padding: "4px 9px", borderRadius: 999, borderWidth: 1, borderStyle: "solid", background: "#fff" },
  toggleChip: { display: "inline-flex", alignItems: "center", gap: 5, fontFamily: fonts.sans, fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 999, borderWidth: 1.4, borderStyle: "solid", background: "#fff", cursor: "pointer", transition: "background 0.15s, opacity 0.15s, color 0.15s" },
  chipMark: { fontSize: 10, fontWeight: 800, lineHeight: 1 },
  draftHead: { display: "flex", alignItems: "center", gap: 7, marginBottom: 8 },
  draftHeadDot: { width: 7, height: 7, borderRadius: 999, background: "#498DCC", boxShadow: "0 0 0 3px rgba(73,141,204,0.18)" },
  draftHeadTitle: { fontFamily: fonts.mono, fontSize: 9.5, letterSpacing: 1.4, fontWeight: 700, color: colors.textPrimary },
  draftHeadHint: { fontFamily: fonts.sans, fontSize: 10, color: colors.textTertiary, marginLeft: "auto" },
  applyBtnDisabled: { background: "rgba(0,0,0,0.08)", color: colors.textTertiary, boxShadow: "none", cursor: "not-allowed" },
  applyBtn: { width: "100%", marginTop: 4, padding: "9px 12px", borderRadius: 999, border: "none", background: "linear-gradient(135deg, #498DCC, #212E20)", color: "#fff", fontFamily: fonts.sans, fontSize: 12.5, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 12px rgba(10,132,255,0.28)" },
  applyBtnDone: { background: "rgba(52,199,89,0.14)", color: colors.ok, boxShadow: "none", cursor: "default" },
  quickRow: { display: "flex", flexWrap: "wrap", gap: 7, padding: "0 16px 10px" },
  quickChip: { fontFamily: fonts.sans, fontSize: 11.5, fontWeight: 500, padding: "7px 11px", borderRadius: 999, border: `1px solid ${colors.border}`, background: "#fff", color: colors.textSecondary, cursor: "pointer" },
  inputRow: { display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", borderTop: `1px solid ${colors.border}`, background: "rgba(255,255,255,0.5)" },
  input: { flex: 1, border: `1px solid ${colors.border}`, borderRadius: 999, padding: "10px 14px", fontFamily: fonts.sans, fontSize: 13.5, color: colors.textPrimary, outline: "none", background: "#fff" },
  sendBtn: { width: 38, height: 38, borderRadius: 999, border: "none", background: colors.textPrimary, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
};
